// Basic
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useToast } from '@/common-utils/hooks/use-toast';
// Store & actions
import actionsOrganizations from '../../api/actionsOrganizations';
import useSessionStore from '@/app/store/session';
// Components
import { Button } from '@/common-utils/components/ui/button';
import ModalDeleteEmail from '../modals/alerts/ModalDeleteEmail';
import Entity from '@/common-utils/components/renders/entity/Entity';
import { Skeleton } from '@/common-utils/components/ui/skeleton';
// Common
import ICONS from '@/common-utils/enums/icons';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';
import Permissions from '@/app/permissions/Permissions';
import useOrganizationsStore from '@/app/store/modules/organizations';
// Lang
import { nsOrganization } from '@/app/i18n/namespaces';

/**
 * @returns {JSX} NewPosConnectionsSection
 */
function NewPosConnectionsSection() {

    // URL Params
    const { organizationId } = useParams();

    // Toast
    const { toast } = useToast();

    // Store
    const { permissions: userPermissions } = useSessionStore(s => s.session) ?? {};
    const { refetchAlertReceivers, storeRefetchAlertReceivers, storeAlertReceivers } = useOrganizationsStore();

    // State
    const [showModalDeleteEmail, setShowModalDeleteEmail] = useState(false);
    const [email, setEmail] = useState('');
    const [launchRefetch, setLaunchRefetch] = useState(false);

    // Api calls
    const { data, isFetching, refetch } = actionsOrganizations.getAlertReceivers(organizationId, true);

    const onSuccess = () => {
        setShowModalDeleteEmail(false);
        setLaunchRefetch(true);
        toast({
            description: <Entity namespace={nsOrganization} entity='modal.delete_mail.success' />,
            variant: 'success'
        });
    };

    const onError = () => {
        setShowModalDeleteEmail(false);
        toast({
            description: <Entity namespace={nsOrganization} entity='toast.error.UNKNOWN_ERROR' />,
            variant: 'danger'
        });
    };
    const deleteAlertReceivers = actionsOrganizations.deleteAlertReceivers(onSuccess, onError);

    // Hooks
    useEffect(() => {
        if (organizationId) {
            refetch();
            setLaunchRefetch(false);
            storeRefetchAlertReceivers(false);
        }
    }, [launchRefetch, refetchAlertReceivers]);

    useEffect(() => {
        if (data) {
            storeAlertReceivers(data);
        }
    }, [data]);

    // Functions
    const handleShowModalDeleteEmail = (item) => {
        setShowModalDeleteEmail(true);
        setEmail(item);
    };

    const handleDeleteEmail = () => {
        deleteAlertReceivers.mutate({
            organizationId: organizationId,
            emails: { emails: [email] }
        });
    };

    return (
        <div className='mt-4'>
            {isFetching &&
                <div
                    className='animate-pulse flex flex-wrap gap-4'>
                    {
                        [1, 2, 3].map((item) => (
                            <Skeleton key={item} className='w-56 rounded-full h-8' />
                        ))
                    }
                </div>
            }
            {
                !isFetching && data &&
                <div className='flex flex-wrap gap-4'>
                    {data.map((mailAdress, index) => (
                        <span
                            key={`${mailAdress}-${index}`}
                            className='py-1 pl-1 pr-2 rounded-full border border-gray-200 bg-secondary-hover flex items-center'
                        >
                            <Button
                                variant='critical'
                                icon={ICONS.CLOSE}
                                dataCy={`delete-pos-alert-mail-${index}`}
                                onClick={() => handleShowModalDeleteEmail(mailAdress)}
                                className='mr-2 p-0 h-6 w-6 rounded-full bg-secondary-hover'
                                disabled={!hasRequiredPermissions({
                                    permissions: userPermissions,
                                    requiredPermissions: [
                                        Permissions.ORGANIZATION_UPDATE_ALERT_POS.name
                                    ]
                                })}
                            />
                            <Entity
                                className='text-sm font-medium'
                                entity={mailAdress}
                            />
                        </span>
                    ))}
                </div>
            }
            {
                showModalDeleteEmail &&
                <ModalDeleteEmail
                    isOpen={showModalDeleteEmail}
                    setIsOpen={setShowModalDeleteEmail}
                    handleValidate={handleDeleteEmail}
                    isLoading={deleteAlertReceivers.isLoading}
                    email={email}
                />
            }
        </div >
    );
}

export default NewPosConnectionsSection;